import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Server } from 'lucide-react';
import { triviaForPacks } from '../content/startupTrivia';
import { getMyTrainEnv, getTrainEnvPassword, mintMyTrainEnvSso, startMyTrainEnv, type TrainEnv } from '../lib/api';
import { formatActiveWhen, mehnatLine } from '../lib/format-duration';
import { readHadMitti, writeHadMitti } from '../lib/mitti-card';
import { packLabel, packStatusLine } from '../lib/mitti-copy';
import {
  STARTING_TAB_MESSAGE,
  START_TIMEOUT_MESSAGE,
  WARMUP_MESSAGE,
  deliverToTab,
  openMittiInGesture,
  treatAsStillStarting,
  urlIfRestarted,
  warmupNoteVisible,
  writeStartingTab,
} from '../lib/train-env-open';
import PackPills from './PackPills';

const POPUP_BLOCKED = 'Allow pop-ups for Akhara, or use the link below';

function errText(code: string | undefined, status: number): string {
  if (status === 0) return 'Network error — check your connection.';
  switch (code) {
    case 'still_provisioning':
      return 'Your Mitti is still being prepared.';
    case 'env_failed':
      return 'This Mitti could not be prepared. Ask your Khalifa for a new one.';
    case 'env_expired':
      return 'This Mitti has expired.';
    case 'rate_limited':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'start_timeout':
      return START_TIMEOUT_MESSAGE;
    case 'start_failed':
      return 'The Mitti did not become ready. Try again in a minute.';
    case 'password_unavailable':
    case 'not_ready':
      return 'The password is not available yet.';
    default:
      return 'Something went wrong. Try again.';
  }
}

/**
 * Learner card on the home page. Hidden when this person has no Mitti.
 * Put in the Mehnat calls start(), which waits until the site answers.
 */
export default function TrainEnvCard() {
  const [env, setEnv] = useState<TrainEnv | null | undefined>(undefined);
  const [hadMitti] = useState(() => readHadMitti());
  const [phase, setPhase] = useState<'idle' | 'starting' | 'open'>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [popupNote, setPopupNote] = useState<string | null>(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const alive = useRef(true);
  const statusEl = useRef<HTMLDivElement | null>(null);
  const packKey = (env?.packs ?? []).join('\n');
  const rotating = useMemo(
    () => triviaForPacks(packKey ? packKey.split('\n') : []).filter((line) => line !== STARTING_TAB_MESSAGE),
    [packKey],
  );
  const [line, setLine] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    void (async () => {
      const res = await getMyTrainEnv();
      if (cancelled) return;
      if (!res.ok) {
        setEnv(null);
        setError(errText((res.data as { error?: string })?.error, res.status));
        return;
      }
      const next = res.data.env;
      writeHadMitti(!!next);
      setEnv(next);
      if (!next) return;
      void getMyTrainEnv({ fresh: true }).then((fresh) => {
        if (cancelled) return;
        if (!fresh.ok) return;
        writeHadMitti(!!fresh.data.env);
        setEnv(fresh.data.env);
      });
    })();
    return () => {
      cancelled = true;
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (phase !== 'starting' || rotating.length === 0) return;
    setLine(0);
    const id = window.setInterval(() => {
      const el = statusEl.current;
      if (!el || !el.isConnected) {
        window.clearInterval(id);
        return;
      }
      setLine((i) => (i + 1) % rotating.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [phase, rotating]);

  if (env === undefined) {
    if (!hadMitti) {
      return (
        <p className="mt-4 text-sm text-ink-400" role="status">
          Checking for a Mitti…
        </p>
      );
    }
    return <PlaceholderCard />;
  }
  if (env === null && !error) return null;
  if (env === null) {
    return <p className="mt-4 text-sm text-ink-500 dark:text-paper-300">{error}</p>;
  }

  const openable = env.state === 'ready' || env.state === 'stopped' || env.state === 'running';
  const mehnat = mehnatLine(env.activeMinutes ?? 0);
  const packsNote = packStatusLine(env.packsInstalling === true, (env.packsPending ?? []).map(packLabel));

  async function freshSso(): Promise<{ action: string; ticket: string } | null> {
    const res = await mintMyTrainEnvSso();
    if (!res.ok || !res.data.action || !res.data.ticket) return null;
    return { action: res.data.action, ticket: res.data.ticket };
  }

  async function openEnv() {
    if (!env) return;
    // Record the stored start time before the click's request. A later poll waits
    // until that value is strictly later. Do not compare it with this machine's clock.
    const previousLastStartedAt = env.lastStartedAt;
    const host = env.host;
    const startedAt = Date.now();
    // Open during the click. A later window.open is blocked after the long start wait.
    // Do not clear opener before the document write; Safari drops the document.
    const tab = window.open('', '_blank');
    if (!tab) {
      setPopupNote(POPUP_BLOCKED);
    } else {
      setPopupNote(null);
      try {
        writeStartingTab(tab, env.packs);
      } catch {
        /* the blank tab can refuse document access */
      }
    }
    setPhase('starting');
    setError(null);
    setOfferOpen(false);
    const res = await startMyTrainEnv();
    if (!alive.current) {
      if (tab && !tab.closed) tab.close();
      return;
    }
    const storedHost = res.data?.host || host;
    if (res.ok && res.data.url) {
      showOpen(res.data.url, storedHost, tab && !tab.closed ? tab : null, res.data.sso);
      return;
    }
    const bodyError = typeof res.data?.error === 'string' ? res.data.error : undefined;
    const stillGoing = treatAsStillStarting(res.status, bodyError, Date.now() - startedAt);
    if (stillGoing) {
      const found = await pollReady(previousLastStartedAt, tab);
      if (!alive.current) {
        if (tab && !tab.closed) tab.close();
        return;
      }
      if (found) {
        // The original start may have been cut at 60s. Take a fresh ticket.
        const sso = found.tab ? await freshSso() : null;
        if (!alive.current) {
          if (found.tab && !found.tab.closed) found.tab.close();
          return;
        }
        showOpen(found.url, storedHost, found.tab, sso);
        return;
      }
    }
    if (tab && !tab.closed) tab.close();
    if (!alive.current) return;
    setPhase('idle');
    setError(errText(bodyError, res.status));
  }

  function showOpen(
    next: string,
    host: string,
    tab: Window | null,
    sso?: { action: string; ticket: string } | null,
  ) {
    setUrl(next);
    setPhase('open');
    if (tab && !tab.closed) {
      deliverToTab(tab, host, sso);
      setOfferOpen(false);
      return;
    }
    // Ready, and the click's tab is missing or closed. Do not call window.open here.
    setOfferOpen(true);
  }

  async function openFromGesture(preopened?: Window) {
    if (!env?.host) return;
    const outcome = await openMittiInGesture({
      openBlank: () => preopened ?? window.open('', '_blank'),
      host: env.host,
      mint: freshSso,
    });
    if (!alive.current) return;
    if (outcome === 'blocked') {
      setPopupNote(POPUP_BLOCKED);
      setOfferOpen(true);
      return;
    }
    setPopupNote(null);
    setOfferOpen(false);
  }

  /**
   * A plain left click signs in like "Put in the Mehnat". Modified clicks keep the real href.
   * The tab is opened inside the click: when pop-ups are blocked, the plain link still opens.
   */
  function onMittiLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const tab = window.open('', '_blank');
    if (!tab) return;
    e.preventDefault();
    void openFromGesture(tab);
  }

  async function pollReady(
    previousLastStartedAt: string | null,
    tab: Window | null,
  ): Promise<{ url: string; tab: Window | null } | null> {
    const deadline = Date.now() + 6 * 60_000;
    let live: Window | null = tab && !tab.closed ? tab : null;
    while (Date.now() < deadline) {
      // A closed tab is no longer polled. The card keeps waiting for ready.
      if (live?.closed) live = null;
      await new Promise((r) => setTimeout(r, 5000));
      if (!alive.current) return null;
      const res = await getMyTrainEnv();
      if (!res.ok || !res.data.env) continue;
      const next = urlIfRestarted(previousLastStartedAt, res.data.env);
      if (next) return { url: next, tab: live && !live.closed ? live : null };
    }
    return null;
  }

  async function revealPassword() {
    if (showPassword) {
      setShowPassword(false);
      return;
    }
    if (!password) {
      setPwBusy(true);
      const res = await getTrainEnvPassword();
      setPwBusy(false);
      if (!alive.current) return;
      if (!res.ok || !res.data.password) {
        setError(errText(res.data?.error, res.status));
        return;
      }
      setPassword(res.data.password);
    }
    setShowPassword(true);
  }

  return (
    <section className="mt-4 min-h-[9.5rem] rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface">
      <div className="flex items-start gap-3">
        <Server className="mt-0.5 h-5 w-5 shrink-0 text-terracotta-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink-900 dark:text-paper-50">Your Mitti</h2>
          <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">
            A private Henoyo site for practice. It stays off until you put in the Mehnat, then it sleeps when you stop.
            A start takes up to 3 minutes.
          </p>
          <div className="mt-2">
            <PackPills packs={env.packs} />
          </div>

          <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">State</dt>
              <dd className="mt-0.5 text-ink-900 dark:text-paper-50">{labelFor(env.state)}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Expires</dt>
              <dd className="mt-0.5 text-ink-900 dark:text-paper-50">{formatActiveWhen(env.expiresAt)}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Last Mehnat</dt>
              <dd className="mt-0.5 text-ink-900 dark:text-paper-50">{formatActiveWhen(env.lastStartedAt)}</dd>
            </div>
          </dl>

          {warmupNoteVisible(env.state, env.lastStartedAt, nowMs) && (
            <p className="mt-2 text-sm text-ink-500 dark:text-paper-300" role="status">
              {WARMUP_MESSAGE}
            </p>
          )}

          {mehnat && <p className="mt-3 text-sm text-ink-800 dark:text-paper-100">{mehnat}</p>}
          {packsNote && (
            <p className="mt-2 text-sm text-ink-700 dark:text-paper-200" role="status">
              {packsNote}
            </p>
          )}

          {env.state === 'provisioning' && (
            <p className="mt-3 text-sm text-ink-700 dark:text-paper-200" role="status">
              Being prepared. Come back and put in the Mehnat when it is ready. A start takes up to 3 minutes.
            </p>
          )}
          {env.state === 'stopped' && (
            <p className="mt-3 text-sm text-ink-700 dark:text-paper-200">
              Good Mehnat. The mitti rests until you're back.
            </p>
          )}
          {env.state === 'expired' && (
            <p className="mt-3 text-sm text-ink-700 dark:text-paper-200">This Mitti has expired.</p>
          )}
          {env.state === 'failed' && (
            <p className="mt-3 text-sm text-ink-700 dark:text-paper-200">
              This Mitti could not be prepared. Ask your Khalifa for a new one.
            </p>
          )}

          {popupNote && (
            <p className="mt-3 text-sm text-ink-700 dark:text-paper-200" role="status">
              {popupNote}
            </p>
          )}

          {phase === 'starting' && (
            <div ref={statusEl} className="mt-3" role="status" aria-live="polite">
              <div
                className="mb-2 h-5 w-5 animate-spin rounded-full border-2 border-paper-200 border-t-terracotta-500"
                aria-hidden
              />
              <p className="text-sm font-medium text-ink-800 dark:text-paper-100">{STARTING_TAB_MESSAGE}</p>
              {rotating.length > 0 && (
                <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">{rotating[line % rotating.length]}</p>
              )}
            </div>
          )}

          {openable && phase !== 'open' && (
            <button
              type="button"
              onClick={() => void openEnv()}
              disabled={phase === 'starting'}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-terracotta-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              Put in the Mehnat
            </button>
          )}

          {phase === 'open' && url && (
            <div className="mt-3 space-y-2 text-sm">
              {offerOpen && (
                <button
                  type="button"
                  onClick={() => void openFromGesture()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-terracotta-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-terracotta-600"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  Open your Mitti
                </button>
              )}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onMittiLinkClick}
                className="inline-flex items-center gap-1 break-all font-medium text-terracotta-700 underline dark:text-terracotta-200"
              >
                {url}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </a>
              <p className="text-ink-700 dark:text-paper-200">
                Login email <span className="break-all font-medium text-ink-900 dark:text-paper-50">{env.email}</span>
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => void revealPassword()}
                  disabled={pwBusy}
                  className="rounded-lg border border-rule px-3 py-1.5 text-sm font-medium text-ink-800 hover:bg-paper-100 disabled:opacity-50 dark:border-clay-rule dark:text-paper-100 dark:hover:bg-clay-raised"
                >
                  {showPassword ? 'Hide password' : pwBusy ? 'Loading…' : 'Show password'}
                </button>
                {showPassword && password && (
                  <code className="break-all rounded bg-paper-100 px-2 py-1 text-xs text-ink-900 dark:bg-clay-floor dark:text-paper-50">
                    {password}
                  </code>
                )}
              </div>
            </div>
          )}

          {error && <p className="mt-2 text-sm text-terracotta-700 dark:text-terracotta-200">{error}</p>}
        </div>
      </div>
    </section>
  );
}

function PlaceholderCard() {
  return (
    <section
      className="mt-4 min-h-[9.5rem] rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <Server className="mt-0.5 h-5 w-5 shrink-0 text-terracotta-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink-900 dark:text-paper-50">Your Mitti</h2>
          <div className="mt-3 flex items-center gap-2">
            <div
              className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-paper-200 border-t-terracotta-500"
              aria-hidden
            />
            <p className="text-sm font-medium text-ink-800 dark:text-paper-100">Getting your Mitti ready…</p>
          </div>
          <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">This takes a few seconds</p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div className="mitti-shimmer h-2.5 w-16 rounded" />
                <div className="mitti-shimmer mt-1.5 h-4 w-28 max-w-full rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function labelFor(state: string): string {
  switch (state) {
    case 'provisioning':
      return 'Being prepared';
    case 'ready':
      return 'Ready';
    case 'running':
      return 'Running';
    case 'stopped':
      return 'Stopped';
    case 'failed':
      return 'Failed';
    case 'expired':
      return 'Expired';
    default:
      return state;
  }
}
